import { isEqual } from "lodash-es";

import { Operation } from "../types/schemas";
import { assertNever } from "./assert-never";

type TransformationPermutation = `${Operation["type"]}-${Operation["type"]}`;

/**
 * Checks if path1 is a direct ancestor of path2
 * A false return means that path 1 is a descendant
 */
function isAncestor(p1: (string | number)[], p2: (string | number)[]): boolean {
  // p1 references root
  if (p1.length === 0 && p2.length !== 0) return true;

  return p1.length < p2.length && p1.every((part, i) => part === p2[i]);
}

/**
 * Finds the index at which the two paths diverge
 */
function getPathIndex(
  p1: (string | number)[],
  p2: (string | number)[],
): number {
  const minLen = Math.min(p1.length, p2.length);
  let divergeIndex = minLen;

  for (let i = 0; i < minLen; i += 1) {
    if (p1[i] !== p2[i]) {
      divergeIndex = i;
      break;
    }
  }

  return divergeIndex;
}

/**
 * Returns updated path object based on op1 array operation
 */
function updatePath(
  op1:
    | Extract<Operation, { type: "array-insert" }>
    | Extract<Operation, { type: "array-delete" }>,
  op2: Operation,
): (number | string)[] {
  const divergeIndex = getPathIndex(op1.path, op2.path);
  const newIndex =
    op1.type === "array-insert"
      ? +op2.path[divergeIndex] + op1.values.length
      : +op2.path[divergeIndex] - (op1.endIndex - op1.startIndex + 1);

  const newPath = [
    ...op2.path.slice(0, divergeIndex),
    newIndex,
    ...op2.path.slice(divergeIndex + 1),
  ];

  return newPath;
}

function getAncestorIndex(op1: Operation, op2: Operation): number {
  const divergeIdx = getPathIndex(op1.path, op2.path);
  return +op2.path[divergeIdx];
}

function objectObjectTransformation(
  op1:
    | Extract<Operation, { type: "object-set" }>
    | Extract<Operation, { type: "object-delete" }>,
  op2:
    | Extract<Operation, { type: "object-set" }>
    | Extract<Operation, { type: "object-delete" }>,
  priority: boolean,
): Operation | null {
  if (isEqual(op1.path, op2.path)) {
    if (op1.type === "object-delete" && op2.type === "object-delete")
      return null;
    return priority ? op2 : null;
  }
  if (isAncestor(op1.path, op2.path)) return null;

  return op2;
}

function objectArrayTransformation(
  op1:
    | Extract<Operation, { type: "object-set" }>
    | Extract<Operation, { type: "object-delete" }>,
  op2:
    | Extract<Operation, { type: "array-insert" }>
    | Extract<Operation, { type: "array-delete" }>,
): Operation | null {
  // object and array ops cannot have the same path so we throw an exception
  if (isEqual(op1.path, op2.path)) {
    return null;
  }
  if (isAncestor(op1.path, op2.path)) return null;

  return op2;
}

function arrayInsertObjectTransformation(
  op1: Extract<Operation, { type: "array-insert" }>,
  op2:
    | Extract<Operation, { type: "object-set" }>
    | Extract<Operation, { type: "object-delete" }>,
): Operation | null {
  // object and array ops cannot have the same path so we throw an exception
  if (isEqual(op1.path, op2.path)) {
    return op2;
  }
  if (isAncestor(op1.path, op2.path)) {
    const ansectorIndex = getAncestorIndex(op1, op2);
    if (ansectorIndex >= op1.index) {
      return { ...op2, path: updatePath(op1, op2) };
    }
  }

  return op2;
}

function arrayInsertArrayInsert(
  op1: Extract<Operation, { type: "array-insert" }>,
  op2: Extract<Operation, { type: "array-insert" }>,
  priority: boolean,
): Operation | null {
  if (isEqual(op1.path, op2.path)) {
    if (op1.index === op2.index) {
      return priority ? op2 : { ...op2, index: op2.index + op1.values.length };
    }
    return op2.index < op1.index
      ? op2
      : { ...op2, index: op2.index + op1.values.length };
  }
  const ancestorIndex = getAncestorIndex(op1, op2);
  if (isAncestor(op1.path, op2.path) && op1.index <= ancestorIndex) {
    return { ...op2, path: updatePath(op1, op2) };
  }

  return op2;
}

function arrayInsertArrayDelete(
  op1: Extract<Operation, { type: "array-insert" }>,
  op2: Extract<Operation, { type: "array-delete" }>,
): Operation | null {
  if (isEqual(op1.path, op2.path)) {
    if (op1.index <= op2.startIndex) {
      return {
        ...op2,
        startIndex: op2.startIndex + op1.values.length,
        endIndex: op2.endIndex + op1.values.length,
      };
    }
    if (op1.index > op2.endIndex) return op2;
    if (op1.index >= op2.startIndex + 1 && op1.index <= op2.endIndex) {
      return {
        ...op2,
        startIndex: op2.startIndex,
        endIndex: op2.endIndex + op1.values.length,
      };
    }
    return op2;
  }

  if (isAncestor(op1.path, op2.path)) {
    const ancestorIndex = getAncestorIndex(op1, op2);
    if (ancestorIndex >= op1.index) {
      return { ...op2, path: updatePath(op1, op2) };
    }
  }
  return op2;
}

function arrayDeleteObjectTransformation(
  op1: Extract<Operation, { type: "array-delete" }>,
  op2:
    | Extract<Operation, { type: "object-set" }>
    | Extract<Operation, { type: "object-delete" }>,
): Operation | null {
  // object and array ops cannot have the same path so we throw an exception
  if (isEqual(op1.path, op2.path)) {
    return op2;
  }
  if (isAncestor(op1.path, op2.path)) {
    const ancestorIndex = getAncestorIndex(op1, op2);

    if (ancestorIndex >= op1.startIndex && ancestorIndex <= op1.endIndex)
      return null;
    if (ancestorIndex > op1.endIndex)
      return { ...op2, path: updatePath(op1, op2) };
  }

  return op2;
}

function arrayDeleteArrayInsert(
  op1: Extract<Operation, { type: "array-delete" }>,
  op2: Extract<Operation, { type: "array-insert" }>,
): Operation | null {
  if (isEqual(op1.path, op2.path)) {
    if (op2.index >= op1.startIndex + 1 && op2.index <= op1.endIndex) {
      return null;
    }
    if (op2.index > op1.endIndex) {
      return { ...op2, index: op2.index - (op1.endIndex - op1.startIndex + 1) };
    }
    return op2;
  }

  if (isAncestor(op1.path, op2.path)) {
    // get the ancestor index
    const ancestorIndex = getAncestorIndex(op1, op2);

    if (ancestorIndex >= op1.startIndex && ancestorIndex <= op1.endIndex)
      return null;
    if (ancestorIndex > op1.endIndex) {
      return { ...op2, path: updatePath(op1, op2) };
    }
  }

  return op2;
}

function arrayDeleteArrayDelete(
  op1: Extract<Operation, { type: "array-delete" }>,
  op2: Extract<Operation, { type: "array-delete" }>,
): Operation | null {
  if (isEqual(op1.path, op2.path)) {
    // Op1 range contains op2 range
    if (op2.startIndex >= op1.startIndex && op2.endIndex <= op1.endIndex)
      return null;

    // Op2 range contains op1 range
    if (op2.startIndex < op1.startIndex && op2.endIndex > op1.endIndex) {
      return {
        ...op2,
        endIndex: op2.endIndex - (op1.endIndex - op1.startIndex + 1),
      };
    }

    // Left of op1 deletes
    if (op2.endIndex < op1.startIndex) return op2;

    // Left-overlap
    if (op2.startIndex <= op1.startIndex && op2.endIndex <= op1.endIndex) {
      return { ...op2, endIndex: op1.startIndex - 1 };
    }

    // Right of op1 deletes
    if (op2.startIndex > op1.endIndex) {
      return {
        ...op2,
        startIndex: op2.startIndex - (op1.endIndex - op1.startIndex + 1),
        endIndex: op2.endIndex - (op1.endIndex - op1.startIndex + 1),
      };
    }

    // Right-overlap
    if (op2.startIndex >= op1.startIndex && op2.endIndex > op1.endIndex) {
      return {
        ...op2,
        startIndex: op1.startIndex,
        endIndex: op2.endIndex - (op1.endIndex - op1.startIndex + 1),
      };
    }
  }

  if (isAncestor(op1.path, op2.path)) {
    // get the ancestor index
    const ancestorIndex = getAncestorIndex(op1, op2);

    if (ancestorIndex >= op1.startIndex && ancestorIndex <= op1.endIndex)
      return null;
    if (ancestorIndex > op1.endIndex) {
      return { ...op2, path: updatePath(op1, op2) };
    }
  }

  return op2;
}

/**
 * Transforms the second operation against the first operation. Operations must be context-equivalent.
 * A transformed operation can become a different operation entirely or a noop.
 * If priority is true, then tie-breaks are won by op2.
 */
export function transformOperation(
  op1: Operation | null,
  op2: Operation | null,
  priority: boolean = true,
): Operation | null {
  // This handles all cases where the paths don't overlap in any way (isSeparate cases)
  if (
    !op1 ||
    !op2 ||
    ("path" in op1 &&
      "path" in op2 &&
      !isEqual(op1.path, op2.path) &&
      !isAncestor(op1.path, op2.path))
  )
    return op2;

  const permutation: TransformationPermutation = `${op1.type}-${op2.type}`;

  switch (permutation) {
    case "object-set-object-set":
      return objectObjectTransformation(
        op1 as Extract<Operation, { type: "object-set" }>,
        op2 as Extract<Operation, { type: "object-set" }>,
        priority,
      );
    case "object-set-object-delete":
      return objectObjectTransformation(
        op1 as Extract<Operation, { type: "object-set" }>,
        op2 as Extract<Operation, { type: "object-delete" }>,
        priority,
      );
    case "object-set-array-insert":
      return objectArrayTransformation(
        op1 as Extract<Operation, { type: "object-set" }>,
        op2 as Extract<Operation, { type: "array-insert" }>,
      );
    case "object-set-array-delete":
      return objectArrayTransformation(
        op1 as Extract<Operation, { type: "object-set" }>,
        op2 as Extract<Operation, { type: "array-delete" }>,
      );
    case "object-delete-object-delete":
      return objectObjectTransformation(
        op1 as Extract<Operation, { type: "object-delete" }>,
        op2 as Extract<Operation, { type: "object-delete" }>,
        priority,
      );
    case "object-delete-object-set":
      return objectObjectTransformation(
        op1 as Extract<Operation, { type: "object-delete" }>,
        op2 as Extract<Operation, { type: "object-set" }>,
        priority,
      );
    case "object-delete-array-insert":
      return objectArrayTransformation(
        op1 as Extract<Operation, { type: "object-delete" }>,
        op2 as Extract<Operation, { type: "array-insert" }>,
      );
    case "object-delete-array-delete":
      return objectArrayTransformation(
        op1 as Extract<Operation, { type: "object-delete" }>,
        op2 as Extract<Operation, { type: "array-delete" }>,
      );
    case "array-insert-object-set":
      return arrayInsertObjectTransformation(
        op1 as Extract<Operation, { type: "array-insert" }>,
        op2 as Extract<Operation, { type: "object-set" }>,
      );
    case "array-insert-object-delete":
      return arrayInsertObjectTransformation(
        op1 as Extract<Operation, { type: "array-insert" }>,
        op2 as Extract<Operation, { type: "object-delete" }>,
      );
    case "array-insert-array-insert":
      return arrayInsertArrayInsert(
        op1 as Extract<Operation, { type: "array-insert" }>,
        op2 as Extract<Operation, { type: "array-insert" }>,
        priority,
      );
    case "array-insert-array-delete":
      return arrayInsertArrayDelete(
        op1 as Extract<Operation, { type: "array-insert" }>,
        op2 as Extract<Operation, { type: "array-delete" }>,
      );
    case "array-delete-object-set":
      return arrayDeleteObjectTransformation(
        op1 as Extract<Operation, { type: "array-delete" }>,
        op2 as Extract<Operation, { type: "object-set" }>,
      );
    case "array-delete-object-delete":
      return arrayDeleteObjectTransformation(
        op1 as Extract<Operation, { type: "array-delete" }>,
        op2 as Extract<Operation, { type: "object-delete" }>,
      );
    case "array-delete-array-insert":
      return arrayDeleteArrayInsert(
        op1 as Extract<Operation, { type: "array-delete" }>,
        op2 as Extract<Operation, { type: "object-insert" }>,
      );
    case "array-delete-array-delete":
      return arrayDeleteArrayDelete(
        op1 as Extract<Operation, { type: "array-delete" }>,
        op2 as Extract<Operation, { type: "array-delete" }>,
      );
    default:
      return assertNever(permutation);
  }
}
